import { Component, OnInit, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { Auth, LoginOutcome } from '../auth';
import { LangSwitch } from '../shared/lang-switch';

@Component({
  selector: 'app-login',
  imports: [TranslatePipe, LangSwitch],
  templateUrl: './login.html',
})
export class Login implements OnInit {
  private auth = inject(Auth);
  private router = inject(Router);

  username = signal('');
  password = signal('');
  totpCode = signal('');
  loading = signal(false);
  oauthLoading = signal<string | null>(null);
  passkeyLoading = signal(false);
  error = signal<string | null>(null);
  mfaTicket = signal<string | null>(null); // 非空 → 显示 TOTP 输入步骤

  // 预取的 passkey challenge —— 见下方 passkey() 的说明。
  private pkBegin: { options_json: string; session_id: string } | null = null;

  ngOnInit(): void {
    // 预取 passkey 的 challenge:点击时才去拿的话,Safari 会因为【用户手势已过期】而拒绝。
    void this.prefetchPasskey();

    // OAuth 登录后仍需 TOTP 时由回调页转交的票据。
    if (typeof sessionStorage !== 'undefined') {
      const t = sessionStorage.getItem('pending_mfa_ticket');
      if (t) {
        sessionStorage.removeItem('pending_mfa_ticket');
        this.mfaTicket.set(t);
      }
    }
  }

  setUsername(e: Event): void {
    this.username.set((e.target as HTMLInputElement).value);
  }
  setPassword(e: Event): void {
    this.password.set((e.target as HTMLInputElement).value);
  }
  setTotp(e: Event): void {
    this.totpCode.set((e.target as HTMLInputElement).value);
  }

  // finish 统一处理登录结果：需 TOTP 则切到二次输入；否则按是否已绑定强因子跳转。
  private finish(outcome: LoginOutcome): void {
    if (outcome.status === 'mfa') {
      this.mfaTicket.set(outcome.mfaTicket ?? null);
      return;
    }
    this.router.navigate([outcome.mfaEnrolled ? '/dashboard' : '/security']);
  }

  async submit(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      this.finish(await this.auth.login(this.username().trim(), this.password()));
    } catch (e: unknown) {
      this.error.set(errMsg(e));
    } finally {
      this.loading.set(false);
    }
  }

  async verifyMfa(): Promise<void> {
    this.loading.set(true);
    this.error.set(null);
    try {
      this.finish(await this.auth.mfaLoginVerify(this.mfaTicket()!, this.totpCode().trim()));
    } catch (e: unknown) {
      this.error.set(errMsg(e));
    } finally {
      this.loading.set(false);
    }
  }

  async oauth(provider: 'google' | 'github'): Promise<void> {
    this.oauthLoading.set(provider);
    this.error.set(null);
    try {
      await this.auth.oauthAuthorize(provider); // 跳转离开本页
    } catch (e: unknown) {
      this.error.set(errMsg(e));
      this.oauthLoading.set(null);
    }
  }

  private async prefetchPasskey(): Promise<void> {
    try {
      this.pkBegin = await this.auth.passkeyLoginBegin();
    } catch {
      this.pkBegin = null; // 拿不到就退回点击时再拿(Chrome 能用,Safari 会失败)
    }
  }

  // ⚠️ 这里【不能】在调 WebAuthn 之前 await 任何东西。
  //
  // Safari 要求 navigator.credentials.get() 在【用户手势的有效期内】被调用。
  // 原来的写法是「点击 → await 拿 challenge → 调 WebAuthn」,那个 await 一回来手势就过期了,
  // Safari 直接抛 NotAllowedError;Chrome 宽松,照样能用 —— 所以只有 Safari 失败。
  //
  // 现在:challenge 在 ngOnInit 里【预取】好,点击时【第一件事】就是调 WebAuthn。
  async passkey(): Promise<void> {
    this.error.set(null);
    const begin = this.pkBegin;
    if (!begin) {
      // 预取失败的兜底(Safari 下这条路仍会失败,但总比什么都不做强)
      this.passkeyLoading.set(true);
      try {
        const b = await this.auth.passkeyLoginBegin();
        const a = await this.auth.passkeyAssert(b);
        this.finish(await this.auth.passkeyLoginFinish(b.session_id, a));
      } catch (e: unknown) {
        this.error.set(errMsg(e));
      } finally {
        this.passkeyLoading.set(false);
      }
      return;
    }

    // ← 手势有效期内,同步发起 WebAuthn
    const assertion = this.auth.passkeyAssert(begin);
    this.passkeyLoading.set(true);
    try {
      this.finish(await this.auth.passkeyLoginFinish(begin.session_id, await assertion));
    } catch (e: unknown) {
      this.error.set(errMsg(e));
      this.pkBegin = null;
      void this.prefetchPasskey(); // challenge 一次性,用过/失败都要换新的
    } finally {
      this.passkeyLoading.set(false);
    }
  }

  cancelMfa(): void {
    this.mfaTicket.set(null);
    this.totpCode.set('');
  }
}

// errMsg 优先取后端返回的业务错误信息（{message}），否则回退通用文案。
// 登录页【绝不】把后端的错误原文贴到界面上。
//
// 曾经会显示：
//   Failed to lookup Client-side Discoverable Credential: error: code = 404
//   reason = USER_NOT_FOUND message = user not found metadata = map[] cause = <nil>
// —— 服务端的错误码、内部术语、甚至 Go 的结构体字段全端到了用户脸上。既难看，
// 又等于告诉攻击者「这个邮箱没注册过」（用户枚举）。
//
// 登录失败对用户只有一个含义：这次登录不成立。真实原因在服务端日志里，运营查得到。
// 只有【用户能自己处理】的少数几类才给出具体提示（验证码错、账户锁定、Passkey 不可用）。
function errMsg(e: unknown, fallback = 'login.failed'): string {
  const reason = errReason(e);
  switch (reason) {
    case 'MFA_CODE_INVALID':
    case 'TOTP_INVALID':
      return 'login.mfaInvalid';
    case 'ACCOUNT_LOCKED':
      return 'login.locked';
    case 'PASSKEY_UNAVAILABLE':
      return 'login.passkeyUnavailable';
    default:
      // 其余一律通用文案：LOGIN_FAILED / PASSKEY_LOGIN_FAILED / 网络错误 / 5xx 都走这里。
      return fallback;
  }
}

// errReason 取后端的错误 reason（Kratos errors 的机器可读码），不取 message。
function errReason(e: unknown): string {
  if (e && typeof e === 'object' && 'error' in e) {
    const er = (e as { error?: { reason?: string } }).error;
    if (er?.reason) return er.reason;
  }
  return '';
}
